import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Avatar,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  IconButton,
  useToast,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  AddIcon,
  InfoIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { useUser } from "../../contexts/UserContext";
import { supabase } from "../../lib/supabaseClient";
import AdminVideoRegisterModal from "../../components/AdminVideoRegisterModal";
import AdminVideoDetailModal from "./AdminVideoDetailModal";
import TrackSelectionModal from "../../components/admin/TrackSelectionModal";
import VideoTracksModal from "../../components/admin/VideoTracksModal";

const PAGE_SIZE = 10;

export default function AdminVideos() {
  const { userInfo } = useUser();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);
  const [selectedVideoForTracks, setSelectedVideoForTracks] = useState(null);
  const [isVideoTracksModalOpen, setIsVideoTracksModalOpen] = useState(false);
  const [selectedVideoForTracksInfo, setSelectedVideoForTracksInfo] =
    useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [allVideos, setAllVideos] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  const toast = useToast();

  const handleCopy = useCallback(
    (code) => {
      navigator.clipboard.writeText(code);
      toast({
        title: "복사 완료!",
        description: "식별코드가 복사되었습니다.",
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "top",
      });
    },
    [toast]
  );

  const handleRowClick = useCallback((video) => {
    setSelectedVideo(video);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedVideo(null);
  }, []);

  const handleEdit = useCallback((videoToEdit) => {
    setSelectedVideo(videoToEdit);
    setRegisterModalOpen(true);
  }, []);

  const handleCloseRegisterModal = useCallback(() => {
    setRegisterModalOpen(false);
    setSelectedVideo(null);
  }, []);

  const handleNewVideoRegistration = useCallback(() => {
    setSelectedVideo(null); // 기존 선택된 비디오 초기화
    setRegisterModalOpen(true);
  }, []);

  const handleAddTracks = useCallback((video) => {
    setSelectedVideoForTracks(video);
    setIsTrackModalOpen(true);
  }, []);

  const handleCloseTrackModal = useCallback(() => {
    setIsTrackModalOpen(false);
    setSelectedVideoForTracks(null);
  }, []);

  const handleTrackAdded = useCallback(() => {
    // 트랙이 추가되면 필요한 경우 데이터를 새로고침할 수 있습니다
    toast({
      title: "트랙이 성공적으로 추가되었습니다.",
      status: "success",
      duration: 2000,
    });
  }, [toast]);

  const handleViewTracks = useCallback((video) => {
    setSelectedVideoForTracksInfo(video);
    setIsVideoTracksModalOpen(true);
  }, []);

  const handleCloseVideoTracksModal = useCallback(() => {
    setIsVideoTracksModalOpen(false);
    setSelectedVideoForTracksInfo(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearch = useCallback(() => {
    setSearchQuery(searchTerm);
    setPage(1); // 검색 시 첫 페이지로 이동
  }, [searchTerm]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  // 검색어에 따른 필터링된 영상 목록
  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) {
      return allVideos;
    }

    const searchLower = searchQuery.toLowerCase();
    return allVideos.filter(
      (video) =>
        video.title?.toLowerCase().includes(searchLower) ||
        video.identification_code?.toLowerCase().includes(searchLower) ||
        video.company?.toLowerCase().includes(searchLower) ||
        video.copyrights_holder_name?.toLowerCase().includes(searchLower) ||
        video.director?.toLowerCase().includes(searchLower) ||
        video.cast?.toLowerCase().includes(searchLower) ||
        video.description?.toLowerCase().includes(searchLower)
    );
  }, [allVideos, searchQuery]);

  // 페이지네이션된 영상 목록
  const paginatedVideos = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredVideos.slice(startIndex, endIndex);
  }, [filteredVideos, page]);

  // 전체 페이지 수 계산
  const totalPagesCount = useMemo(() => {
    return Math.ceil(filteredVideos.length / PAGE_SIZE);
  }, [filteredVideos]);

  // 작품 리스트 조회 (company 조건 제거)
  useEffect(() => {
    async function fetchVideos() {
      setLoading(true);
      try {
        // 전체 데이터 조회
        const { data, error } = await supabase
          .from("licensed_video")
          .select("*")
          .order("created_date", { ascending: false });

        if (error) {
          console.error("영상 데이터 가져오기 실패:", error);
        } else {
          setAllVideos(data || []);
        }
      } catch (error) {
        console.error("영상 데이터 가져오기 실패:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchVideos();
  }, []);

  // 페이지 변경 시 페이지 수 업데이트
  useEffect(() => {
    setTotalPages(totalPagesCount);
  }, [totalPagesCount]);

  // 메모이제이션된 값들
  const isRegisterModalOpen = useMemo(
    () => registerModalOpen,
    [registerModalOpen]
  );

  // 영상 등록/수정 완료 후 콜백 함수
  const handleVideoRegistrationComplete = useCallback(() => {
    // 페이지 새로고침
    window.location.reload();
  }, []);

  // 영상 삭제 함수
  const handleDeleteVideo = async () => {
    if (!videoToDelete) return;
    try {
      const { error } = await supabase
        .from("licensed_video")
        .delete()
        .eq("id", videoToDelete.id);
      if (error) throw error;
      toast({
        title: "영상이 삭제되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setIsDeleteModalOpen(false);
      setVideoToDelete(null);
      window.location.reload();
    } catch (e) {
      toast({
        title: e.message || "영상 삭제 실패",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="#f7fafd">
      <Flex align="center" justify="space-between" mb={6}>
        <Heading as="h1" size="lg">
          영상 관리
        </Heading>
        <Flex align="center" gap={2}>
          <Button
            colorScheme="blue"
            size="md"
            onClick={handleNewVideoRegistration}
          >
            영상 등록
          </Button>
        </Flex>
      </Flex>

      {/* 검색 필드 */}
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        <Flex mb={4}>
          <InputGroup maxW="400px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="영상 제목, 식별코드, 회사, 권리자로 검색"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
            />
          </InputGroup>
          <Button ml={2} onClick={handleSearch}>
            검색
          </Button>
        </Flex>
        {searchQuery && (
          <Text fontSize="sm" color="gray.600">
            검색 결과: {filteredVideos.length}개 영상
          </Text>
        )}
      </Box>

      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md">
        {loading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="md">
              <Thead>
                <Tr>
                  <Th style={{ flex: 5, maxWidth: "300px" }}>타이틀</Th>
                  <Th
                    style={{ flex: 1.25, minWidth: 0 }}
                    display={{ base: "none", md: "table-cell" }}
                  >
                    회사
                  </Th>
                  <Th
                    style={{ flex: 1.25, minWidth: 0 }}
                    display={{ base: "none", md: "table-cell" }}
                  >
                    공개상태
                  </Th>
                  <Th
                    style={{ flex: 1.25, minWidth: 0 }}
                    display={{ base: "none", md: "table-cell" }}
                  >
                    식별코드
                  </Th>
                  <Th
                    style={{ flex: 1.25, minWidth: 0 }}
                    display={{ base: "none", md: "table-cell" }}
                  >
                    권리자
                  </Th>
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedVideos.map((video) => (
                  <Tr key={video.id}>
                    <Td style={{ flex: 5, maxWidth: "400px" }}>
                      <Flex align="center" gap={3}>
                        <Box
                          w="100%"
                          h="100%"
                          maxW="80px"
                          bg="white"
                          borderRadius="md"
                          overflow="hidden"
                        >
                          {video.thumbnail && (
                            <img
                              src={video.thumbnail}
                              alt="thumb"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                background: "#fff",
                              }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Text fontWeight="bold">{video.title}</Text>
                          {video.published_year && (
                            <Text color="gray.600" fontSize="xs">
                              {video.published_year}
                            </Text>
                          )}
                          {video.introduction && (
                            <Text color="gray.500" fontSize="xs" mt={1}>
                              {video.introduction}
                            </Text>
                          )}
                          {video.director && (
                            <Text color="gray.500" fontSize="xs" mt={1}>
                              <b>감독:</b> {video.director}
                            </Text>
                          )}
                          {video.cast && (
                            <Text color="gray.500" fontSize="xs" mt={1}>
                              <b>출연:</b> {video.cast}
                            </Text>
                          )}
                          <Text color="gray.500" fontSize="lg" noOfLines={2}>
                            {video.description}
                          </Text>
                        </Box>
                      </Flex>
                    </Td>
                    <Td
                      fontSize="xs"
                      style={{ flex: 1.25, minWidth: 0 }}
                      display={{ base: "none", md: "table-cell" }}
                    >
                      {video.company || "-"}
                    </Td>
                    <Td
                      fontSize="xs"
                      style={{ flex: 1.25, minWidth: 0 }}
                      display={{ base: "none", md: "table-cell" }}
                    >
                      <Box
                        as="span"
                        px={2}
                        py={1}
                        borderRadius="md"
                        fontWeight="bold"
                        fontSize="xs"
                        color={video.open === true ? "white" : "gray.700"}
                        bg={video.open === true ? "blue.400" : "gray.200"}
                        display="inline-block"
                        minW="40px"
                        textAlign="center"
                      >
                        {video.open === true ? "공개" : "비공개"}
                      </Box>
                    </Td>
                    <Td
                      fontSize="xs"
                      style={{ flex: 1.25, minWidth: 0 }}
                      display={{ base: "none", md: "table-cell" }}
                    >
                      <Box
                        as="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(video.identification_code);
                        }}
                        cursor="pointer"
                        color="blue.500"
                        fontWeight="bold"
                        _hover={{ textDecoration: "underline" }}
                        bg="gray.50"
                        borderRadius="md"
                        px={2}
                        py={1}
                      >
                        {video.identification_code}
                      </Box>
                    </Td>
                    <Td
                      fontSize="xs"
                      style={{ flex: 1.25, minWidth: 0 }}
                      display={{ base: "none", md: "table-cell" }}
                    >
                      {video.copyrights_holder_name || "-"}
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <Button
                          size="xs"
                          leftIcon={<EditIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(video);
                          }}
                        >
                          수정
                        </Button>
                        <Button
                          size="xs"
                          leftIcon={<AddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddTracks(video);
                          }}
                        >
                          트랙 추가
                        </Button>
                        <Button
                          size="xs"
                          leftIcon={<InfoIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTracks(video);
                          }}
                        >
                          트랙 정보
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            setVideoToDelete(video);
                            setIsDeleteModalOpen(true);
                          }}
                        >
                          삭제
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {/* 페이지네이션 */}
            <Flex justify="center" align="center" mt={6} gap={2}>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1}
              />
              <Text fontWeight="bold" fontSize="sm">
                {page}/{totalPages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages}
              />
            </Flex>
          </>
        )}
      </Box>
      <AdminVideoRegisterModal
        isOpen={registerModalOpen}
        onClose={handleCloseRegisterModal}
        video={selectedVideo}
        userInfo={userInfo}
        company={null}
        onComplete={handleVideoRegistrationComplete}
      />
      {selectedVideo && (
        <AdminVideoDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetailModal}
          video={selectedVideo}
          onEdit={handleEdit}
        />
      )}
      <TrackSelectionModal
        isOpen={isTrackModalOpen}
        onClose={handleCloseTrackModal}
        videoId={selectedVideoForTracks?.id}
        onTrackAdded={handleTrackAdded}
      />
      <VideoTracksModal
        isOpen={isVideoTracksModalOpen}
        onClose={handleCloseVideoTracksModal}
        videoId={selectedVideoForTracksInfo?.id}
        videoTitle={selectedVideoForTracksInfo?.title}
      />
      {/* 삭제 확인 모달 */}
      <Box>
        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} isCentered>
          <Box as={ModalOverlay} />
          <Box as={ModalContent}>
            <ModalHeader>영상 삭제 확인</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>정말로 이 영상을 삭제하시겠습니까?</Text>
              <Text fontWeight="bold" mt={2}>{videoToDelete?.title}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsDeleteModalOpen(false)}>
                취소
              </Button>
              <Button colorScheme="red" onClick={handleDeleteVideo}>
                삭제
              </Button>
            </ModalFooter>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}
